import {
  useEffect,
  useRef,
  useState,
  MouseEvent as ReactMouseEvent,
} from 'react'

import { useNavigate } from 'react-router-dom'

import { gaSendCustomEvent } from 'analytics'
import { useAudioContext, useAudioPositionContext } from 'audioContext'
import { A11yText } from 'components/A11yText'
import { IconButton } from 'components/IconButton'
import { Image } from 'components/Image'
import { Loading } from 'components/Loading'
import { PlayerDetails } from 'components/PlayerDetails'
import {
  IconPause,
  IconPlay,
  IconSkipBack,
  IconSkipForward,
  IconClose,
} from 'components/icons'
import { convertDurationString } from 'hooks/useBundle'
import { getLanguage, setLanguage as setStorageLanguage } from 'storage'
import { AudioStory, AudioBlock } from 'types/contentful'
import { useSettings } from 'utils/settings'
import { useTranslation } from 'utils/translation'

import {
  PlayButton,
  Controls,
  Wrapper,
  PlayerTime,
  Duration,
  PlayerTitle,
  MetaBlock,
  PlayerContainer,
  SkipButton,
  PlayerImage,
} from '../styles'
import { Error } from './Error'
import { PlayingIndicator } from './PlayingIndicator'
import { SeekBar } from './SeekBar'
import { Time, TimeRemaining } from './Time'

export const durationTime = (secondsInput: number) => {
  const hours = Math.floor(secondsInput / 3600)
  const minutes = Math.floor((secondsInput % 3600) / 60)
  const seconds = Math.floor((secondsInput % 3600) % 60)

  return `${hours > 0 ? `${hours} hours` : ''}${minutes} mins ${
    seconds < 10 ? `0${seconds}` : `${seconds}`
  } sec`
}

const TimeDetails = ({ loaded }: { loaded: boolean }) => {
  const { progress } = useAudioPositionContext()
  return (
    <PlayerTime>
      <Time loaded={loaded} progress={progress.current} />
      <Time
        loaded={loaded}
        progress={progress.remaining > 0 ? progress.remaining : 0}
      />
    </PlayerTime>
  )
}

const TimeRemainingDetails = ({ loaded }: { loaded: boolean }) => {
  const { progress } = useAudioPositionContext()
  return (
    <TimeRemaining
      loaded={loaded}
      progress={progress.remaining > 0 ? progress.remaining : 0}
    />
  )
}

export const MainPlayer = ({
  audiosCollection,
  largeImage,
  // slug,
  small = false,
}: Omit<AudioStory, 'sys' | 'contentfulMetadata' | 'related'> & {
  small?: boolean
}) => {
  const { t } = useTranslation()
  const wrapperRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const { settings } = useSettings()
  const {
    error,
    loaded,
    playing,
    onSeek,
    onPlayButtonClick,
    setAudioUrl,
    skip,
    // setAudioStoryId,
    audioStoryId,
    duration: durationContext,
    playableStartSent,
    setShowPlayer,
  } = useAudioContext()

  const { progress } = useAudioPositionContext()

  const [language, setLanguage] = useState(getLanguage())
  const [audioDetails, setAudioDetails] = useState<AudioBlock>()

  const getAudioByLanguage = (lang: string) => {
    return audiosCollection?.items.find((item) => item?.language === lang)
  }

  const getAudioStoryLanguages = () => {
    return (
      audiosCollection?.items
        .map((item) => item?.language)
        .filter((item): item is string => !!item) || []
    )
  }

  const sendDuration = () => {
    if (playableStartSent) {
      console.log(`send duration ${progress.current}`)
      gaSendCustomEvent(`stories/${audioStoryId}/${language}/duration`, {
        value: progress.current,
      })
    }
  }

  useEffect(() => {
    if (language) {
      const aud = getAudioByLanguage(language)

      // Restore to default content language.
      if (!aud) {
        setStorageLanguage(settings.defaultContentLanguage)
        return
      }

      if (aud && aud?.audio?.url) {
        setAudioUrl(aud.audio.url)
        setAudioDetails(aud)
      }
    }
  }, [])

  useEffect(() => {
    if (language) {
      const aud = getAudioByLanguage(language)
      if (aud && aud?.audio?.url) {
        setAudioUrl(aud.audio.url)
        setAudioDetails(aud)
      }
    }
  }, [language, audiosCollection])

  const onLanguage = () => {
    if (playing) sendDuration()
    setLanguage(getLanguage())
  }

  // Listen to the localstorage change events.
  useEffect(() => {
    document.addEventListener('language-change', onLanguage)
    // if (slug) setAudioStoryId(slug)
    return () => {
      document.removeEventListener('language-change', onLanguage)
    }
  }, [])

  const onClose = () => {
    sendDuration()
    setShowPlayer(false)
    setAudioUrl(undefined)
  }

  const openStory = (event: ReactMouseEvent<HTMLElement>) => {
    if (
      wrapperRef?.current === event.target &&
      small &&
      window.innerWidth < 960
    ) {
      navigate(`/stories/${audioStoryId}`)
    }
  }

  useEffect(() => {
    window.addEventListener('beforeunload', sendDuration)
    return () => {
      window.removeEventListener('beforeunload', sendDuration)
    }
  }, [playing])

  return (
    <>
      {error ? (
        <Error />
      ) : (
        <>
          {!small && (
            <PlayerImage>
              <Image
                alignment="left"
                assets={[{ ...largeImage, language: 'en' }]}
                alt={largeImage?.description || ''}
              />
            </PlayerImage>
          )}
          <Wrapper
            ref={wrapperRef}
            small={small}
            onClick={(event) => openStory(event)}
          >
            {small && <PlayingIndicator playing={playing} />}
            <MetaBlock small={small}>
              <PlayerTitle className="light" allowEllipsis={small}>
                {audioDetails?.title}
              </PlayerTitle>
              <Duration>
                <span>
                  {(audioDetails?.duration &&
                    convertDurationString(audioDetails.duration)) ||
                    (durationContext && durationTime(durationContext))}
                </span>
                {small && <TimeRemainingDetails loaded={loaded} />}
              </Duration>
            </MetaBlock>
            <PlayerContainer small={small}>
              <Controls small={small}>
                <SkipButton
                  secondary
                  disabled={!loaded}
                  title={t('player.controls.skip-back')}
                  aria-label={t('player.controls.skip-back')}
                  icon={IconSkipBack}
                  iconSize="medium"
                  onClick={() => skip(-15)}
                >
                  <span>15</span>
                  <A11yText>{t('player.controls.skip-back')}</A11yText>
                </SkipButton>

                <PlayButton
                  onClick={onPlayButtonClick}
                  disabled={!loaded || !onPlayButtonClick}
                  title={
                    playing
                      ? t('player.controls.playing')
                      : t('player.controls.paused')
                  }
                  aria-label={
                    playing
                      ? t('player.controls.playing')
                      : t('player.controls.paused')
                  }
                >
                  {!loaded ? (
                    <Loading />
                  ) : (
                    <>
                      {playing ? (
                        <IconPause size="small" />
                      ) : (
                        <IconPlay size="small" />
                      )}
                    </>
                  )}
                </PlayButton>
                <SkipButton
                  secondary
                  disabled={!loaded}
                  icon={IconSkipForward}
                  title={t('player.controls.skip-forward')}
                  aria-label={t('player.controls.skip-forward')}
                  onClick={() => skip(15)}
                  iconSize="medium"
                >
                  <span>15</span>
                  <A11yText>{t('player.controls.skip-forward')}</A11yText>
                </SkipButton>
              </Controls>
              <SeekBar loaded={loaded} progress={progress} onSeek={onSeek} />
              <TimeDetails loaded={loaded} />
            </PlayerContainer>
            <PlayerDetails
              id={audioStoryId}
              availableLanguages={getAudioStoryLanguages()}
              credits={audioDetails?.credits}
              transcript={audioDetails?.transcript}
              small={small}
            />
            {small && (
              <IconButton
                iconSize="small"
                title={t('player.close')}
                onClick={onClose}
                icon={IconClose}
              />
            )}
          </Wrapper>
        </>
      )}
    </>
  )
}
